/* eslint-disable react/jsx-props-no-spreading */
import classnames from 'classnames';
import * as React from 'react';
import { Link, To } from 'react-router-dom';
import { Color } from 'palette/Colors';
import { usePaletteConfig } from 'palette/Config';
import { Icon, IconName } from 'palette/Icon';
import { LogoName } from 'palette/Logo';
import { Typography } from 'palette/Typography';
import { ButtonLoader } from './ButtonLoader';
import {
    ICON_SIZE_BY_BUTTON_SIZE, IconPosition, Shape, Size, TYPOGRAPHY_PROPS_BY_SIZE, Theme,
} from './constants';
import './styles/Button.scss';

type Filter<K, R> = Pick<K, Exclude<keyof K, keyof R>>;

interface GeneralProps<T> {
    label?: string;
    theme?: Theme;
    size?: Size;
    shape?: Shape;
    iconName?: IconName | LogoName;
    iconColor?: Color;
    isExpanded?: boolean;
    isLoading?: boolean;
    iconPosition?: IconPosition;
    horizontalAlignment?: 'left' | 'center' | 'right' | 'space-between';
    disabled?: boolean;
    className?: string;
    href?: T;
    to?: To;
    target?: string;
    isTransparent?: boolean;
    testId?: string;
    svgUrl?: string;
    ariaLabel?: string;
    ariaHasPopup?: boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
}

export type ButtonType = 'button' | 'submit' | 'reset';

export interface ButtonProps<T>
    extends GeneralProps<T>,
    React.HTMLAttributes<HTMLButtonElement> {
    type?: ButtonType;
}

export interface ButtonLinkProps<T>
    extends GeneralProps<T>,
    React.HTMLAttributes<HTMLAnchorElement> {
    type?: string;
}

type RefProp<T> = T extends string
    ? React.Ref<HTMLAnchorElement>
    : React.Ref<HTMLButtonElement>;
type Props<T> = T extends string ? ButtonLinkProps<T> : ButtonProps<T>;

const Button = React.forwardRef(
    <T extends string>(
        {
            label,
            iconName,
            iconColor,
            theme: componentTheme = 'primary',
            size = 'medium',
            shape = 'rectangle',
            disabled = false,
            isExpanded = false,
            isLoading = false,
            iconPosition = 'right',
            type,
            className,
            children,
            horizontalAlignment = 'center',
            href,
            to,
            testId,
            isTransparent = false,
            svgUrl,
            ariaLabel,
            ariaHasPopup,
            ...restProps
        }: Props<T>,
        ref?: RefProp<T>,
    ) => {
        const { theme: paletteTheme } = usePaletteConfig();
        const hasLabel = label !== undefined && label !== '';
        const isIconOnly = !hasLabel && !children && iconName;

        const theme = componentTheme === 'primary'
            ? `${componentTheme}-${paletteTheme}`
            : componentTheme;
        const modifiers = [
            theme ? `${theme}${isTransparent ? '-transparent' : ''}${isIconOnly ? '-icon-only' : ''}` : null,
            size,
            shape,
            isExpanded ? 'expanded' : null,
            isLoading ? 'loading' : null,
            iconName ? `icon-position-${iconPosition}` : null,
            horizontalAlignment ? `horizontal-align-${horizontalAlignment}` : null,
            disabled ? `${theme}-disabled` : null,
        ]
            .filter(item => item !== null)
            .map(item => `palette-button--${item}`)
            .join(' ');
        const classes = classnames('palette-button', modifiers, className);
        const commonProps = {
            className: classes,
            'data-testid': testId,
        };

        const content = (): JSX.Element => (
            <>
                {isLoading && <ButtonLoader />}
                {!isLoading && hasLabel && shape !== 'round' ? (
                    <Typography
                        className="palette-button__label"
                        color="inherit"
                        overflowType="ellipsis"
                        {...TYPOGRAPHY_PROPS_BY_SIZE[size]}
                    >
                        {label}
                    </Typography>
                ) : (
                    children
                )}
                {!isLoading && iconName ? (
                    <Icon
                        className="palette-button__icon"
                        color={iconColor}
                        name={iconName}
                        size={ICON_SIZE_BY_BUTTON_SIZE[size]}
                        svgUrl={svgUrl}
                    />
                ) : null}
            </>
        );

        const handleLinkClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
            if (disabled) {
                e.preventDefault();
                return;
            }

            restProps.onClick?.(e);
        };

        const anchor = () => (
            <a
                {...(ref && ({ ref } as { ref: React.Ref<HTMLAnchorElement> }))}
                {...commonProps}
                aria-label={ariaLabel}
                type={type as string}
                {...(restProps as Filter<typeof restProps, React.HTMLAttributes<HTMLAnchorElement>>)}
                href={href}
                onClick={handleLinkClick}
            >
                {content()}
            </a>
        );

        const link = () => (
            <Link
                {...(ref && ({ ref } as { ref: React.Ref<HTMLAnchorElement> }))}
                {...commonProps}
                {...(restProps as Filter<typeof restProps, React.HTMLAttributes<HTMLButtonElement>>)}
                to={to!}
            >
                {content()}
            </Link>
        );

        const button = () => (
            <button
                {...(ref && ({ ref } as { ref: React.Ref<HTMLButtonElement> }))}
                {...commonProps}
                {...(restProps as Filter<typeof restProps, React.HTMLAttributes<HTMLButtonElement>>)}
                aria-haspopup={ariaHasPopup}
                aria-label={ariaLabel}
                disabled={disabled}
                type={(type ?? 'button') as ButtonType}
            >
                {content()}
            </button>
        );

        if (href) { return anchor(); }
        if (to) { return link(); }

        return button();
    },
);

Button.displayName = 'Button';

export default Button as <T>(
    props: Props<T> & { ref?: RefProp<T> }
) => JSX.Element;
